import {
  Directive,
  Inject,
  Injectable,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { map } from 'rxjs/operators';

export const URL_QUERY = new InjectionToken<Subject<UrlQuery>>('urlQuery');

export type UrlQuery = { [key: string]: string | undefined };

export function subject() {
  return new BehaviorSubject(undefined);
}

@Injectable()
export class UrlQueryFilter {
  readonly matches!: Observable<boolean>;

  constructor(
    private readonly route: ActivatedRoute,
    @Inject(URL_QUERY) private readonly urlQuery: Observable<UrlQuery>,
  ) {
    this.matches = combineLatest([
      this.route.queryParamMap,
      this.urlQuery,
    ]).pipe(
      map(([params, urlQuery]) => {
        for (const key in urlQuery) {
          if (urlQuery[key] != params.get(key)) {
            return false;
          }
        }
        return true;
      }),
    );
  }
}

@Directive({
  selector: '[urlQuery]',
  providers: [UrlQueryFilter, { provide: URL_QUERY, useFactory: subject }],
})
export class UrlQueryDirective implements OnDestroy, OnInit {
  private subscription: Subscription | undefined;
  private view: ViewRef | undefined;

  constructor(
    @Inject(URL_QUERY) private readonly urlQuery_: Subject<UrlQuery>,
    private readonly template: TemplateRef<any>,
    private readonly urlQueryFilter: UrlQueryFilter,
    private readonly viewContainer: ViewContainerRef,
  ) {}

  ngOnInit() {
    this.subscription = this.urlQueryFilter.matches.subscribe(matches => {
      if (matches) {
        if (!this.view) {
          this.view = this.viewContainer.createEmbeddedView(this.template);
        }
      } else if (this.view) {
        this.view.destroy();
        this.view = undefined;
      }
    });
  }

  @Input()
  set urlQuery(value: UrlQuery) {
    this.urlQuery_.next(value);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
